import { TrashIcon } from '@phosphor-icons/react';

import { palette } from '@/lib/design-tokens';
import type { MapPoint } from '@/lib/maps/types';

import { formatCoordinate } from '../format';

import styles from './marked-points.module.scss';

export type MapMarkerTooltipProps = {
  /** What the marker is — `"Point 3"`, or `"Mid Point"`. */
  title: string;
  /** The coordinate shown beneath the title. Rounded for display, unlike the panel's own cells. */
  point: MapPoint;
  /** Whether a delete control is offered at all. `false` at exactly three **Point**s (grilling
   *  **D7** — absent, not greyed); always `true` for a **Mid Point**, which has no minimum. */
  canDelete: boolean;
  /** The control's accessible name — `"Delete point 3"` or `"Delete Mid Point"`. Passed rather than
   *  derived from `title`, so neither caller's wording is the other's business. */
  deleteLabel: string;
  onDelete: () => void;
};

/**
 * The dark tooltip a marker on the Field Mapping canvas opens — a **Point**'s (113600) and a **Mid
 * Point**'s (113601).
 *
 * **UNDESIGNED in Figma** (grilling Q11): every value comes from the dark appearance of this repo's
 * own `<Tooltip>`, so the two dark tooltips in this app are the same tooltip. It keeps
 * `marked-points.module.scss` as its stylesheet rather than taking a copy — the same
 * one-stylesheet-per-family arrangement `map-search.module.scss` and `dialog.module.scss` already
 * use — so the Point tooltip and the Mid Point tooltip cannot drift apart.
 *
 * A sibling overlay inside its caller's `<MapOverlay>`, **not** the shared antd `<Tooltip>`: it has
 * to move with its marker on every camera frame, which a floating popup does not subscribe to.
 *
 * No `role="tooltip"`: it contains a real `<button>`, and WAI-ARIA forbids focusable content inside
 * that role.
 */
export function MapMarkerTooltip({
  title,
  point,
  canDelete,
  deleteLabel,
  onDelete,
}: MapMarkerTooltipProps) {
  return (
    <span className={styles.tooltip}>
      <span className={styles.tooltipHeader}>
        <span>{title}</span>
        {canDelete ? (
          <button
            type="button"
            className={styles.deleteButton}
            aria-label={deleteLabel}
            onClick={onDelete}
          >
            <TrashIcon size={16} fill={palette.error[700]} aria-hidden />
          </button>
        ) : null}
      </span>
      <span className={styles.coords}>
        <span>{formatCoordinate(point.lat)}</span>
        <span>{formatCoordinate(point.lon)}</span>
      </span>
      <span className={styles.arrow} aria-hidden />
    </span>
  );
}
